import React, { useEffect, useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import { WHATSAPP_URL } from "../lib/constants";

/**
 * SchoolQuoteModal — school-focused quote form (Educacional / Formaturas).
 * Adaptado do CorporateQuoteModal para nomenclatura pedagógica.
 *
 * Props:
 *   onClose:    fn to close the modal
 *   variant:    "educacional" | "formatura"  → controla labels e prompts do WhatsApp
 */
const CFG = {
  educacional: {
    eyebrow: "◈ Cotação · Excursão Escolar",
    title: "Solicitar Cotação · Excursão Escolar",
    subtitle: "Monte o pedido em 60 segundos — a curadoria pedagógica devolve com proposta didática + logística + segurança.",
    contactLabel: "Nome do responsável (coordenação/direção)",
    institutionLabel: "Escola / Instituição",
    segmentLabel: "Segmento de ensino",
    segmentOptions: [
      "Ensino Fundamental II (6º ao 9º)",
      "Ensino Médio",
      "Ensino Técnico",
      "Ensino Superior / Universidade",
      "Formação de Professores",
      "Outro",
    ],
    participantsLabel: "Número de alunos",
    goalLabel: "Objetivo pedagógico",
    goalPlaceholder: "Ex.: Estudo do meio Amazônia, missão empreendedora, projeto de sustentabilidade…",
    destinationLabel: "Destino de interesse (se já pensado)",
    destinationPlaceholder: "Ex.: Chapada Diamantina, Foz do Iguaçu, São Paulo…",
    ctaSummaryWa: "*Solicitação de Cotação · Excursão Escolar*",
    savingsLine: "(Grupos escolares a partir de 20 alunos têm até 45% de desconto e parceria anual de ônibus com o melhor R$/km rodado.)",
  },
  formatura: {
    eyebrow: "◈ Cotação · Viagem de Formatura",
    title: "Solicitar Cotação · Formatura",
    subtitle: "Monte o pedido em 60 segundos — devolvemos um plano detalhado com destinos, ativações e parcelamento em até 24×.",
    contactLabel: "Nome do responsável dos organizadores",
    institutionLabel: "Escola / Curso / Turma",
    segmentLabel: "Momento da formatura",
    segmentOptions: [
      "Ensino Médio · Formatura",
      "Graduação · Formatura",
      "Pós-graduação · Formatura",
      "Formatura Técnica",
      "Ainda estamos pesquisando",
    ],
    participantsLabel: "Número de formandos",
    goalLabel: "O que a turma quer da viagem",
    goalPlaceholder: "Ex.: Balada open-bar em Camboriú, praia e all-inclusive em Punta Cana, roteiro cultural…",
    destinationLabel: "Destino de interesse (se já pensado)",
    destinationPlaceholder: "Ex.: Porto Seguro, Camboriú, Cancún, Punta Cana, Buenos Aires…",
    ctaSummaryWa: "*Solicitação de Cotação · Viagem de Formatura*",
    savingsLine: "(Turmas a partir de 25 formandos têm parcelamento em até 24× e financiamento próprio via parceiros Orion.)",
  },
  intercambio: {
    eyebrow: "◈ Cotação · Intercâmbio",
    title: "Solicitar Cotação · Intercâmbio",
    subtitle: "Preencha em 60 segundos — a consultoria da Orion volta com programa sugerido, escolas parceiras, orçamento e cronograma personalizado.",
    contactLabel: "Nome completo do intercambista",
    institutionLabel: "Cidade e estado onde mora",
    segmentLabel: "Momento da vida",
    segmentOptions: [
      "Ensino Médio (High School)",
      "Universitário / Graduação",
      "Pós-graduação / Mestrado",
      "Profissional (trabalho + estudo)",
      "Sabático / Ano de gap",
      "Junior / Teen Camp (12 a 17)",
    ],
    participantsLabel: "Ano de nascimento",
    goalLabel: "Objetivo do intercâmbio",
    goalPlaceholder: "Ex.: Fluência em inglês, cursar universidade no exterior, trabalhar como au pair, imersão cultural…",
    destinationLabel: "Destino de interesse",
    destinationPlaceholder: "Ex.: Irlanda, Canadá, Malta, EUA, Reino Unido, Austrália…",
    ctaSummaryWa: "*Solicitação de Cotação · Intercâmbio*",
    savingsLine: "(Programas Orion incluem escola parceira, visto, seguro-saúde internacional e mentoria pré-embarque.)",
  },
  cruzeiros: {
    eyebrow: "◈ Cotação · Cruzeiros",
    title: "Solicitar Cotação · Cruzeiros",
    subtitle: "Preencha em 60 segundos — a consultoria Orion volta com 2 a 3 cruzeiros comparados, transfer SP→Santos incluso e cronograma de pagamento.",
    contactLabel: "Nome completo",
    institutionLabel: "Cidade e estado onde mora",
    segmentLabel: "Perfil do cruzeiro",
    segmentOptions: [
      "Família com crianças",
      "Casal / Lua de mel",
      "3ª idade / Sênior",
      "Cruzeiro temático (gospel, sertanejo, LGBT+)",
      "Cruzeiro empresarial / grupo fechado",
      "Cruzeiro internacional (Mediterrâneo, Caribe, Alaska)",
      "Ainda estamos pesquisando",
    ],
    participantsLabel: "Nº de passageiros",
    goalLabel: "O que a família / grupo espera do cruzeiro",
    goalPlaceholder: "Ex.: 7 noites all-inclusive, cabine com varanda, transfer SP→Santos, jantar temático, kids club…",
    destinationLabel: "Destino / roteiro de interesse",
    destinationPlaceholder: "Ex.: Costa Brasileira, Caribe, Mediterrâneo, Fiordes Noruegueses, Alaska…",
    ctaSummaryWa: "*Solicitação de Cotação · Cruzeiros*",
    savingsLine: "(Cruzeiros Orion incluem transfer ida/volta SP↔Santos, seguro-viagem, consultor no WhatsApp 24h a bordo.)",
  },
};

// Options exclusivas do fluxo intercâmbio
const DECISOR_OPTIONS = [
  "Eu decido sozinho e tenho autonomia financeira",
  "Eu decido com apoio financeiro de terceiros",
  "Eu não decido sozinho (dependo de aprovação do responsável)",
];
const EMBARQUE_OPTIONS = [
  "Até 6 meses",
  "De 7 meses a 1 ano",
  "De 1 ano a 2 anos",
  "Acima de 2 anos",
];
const INVEST_OPTIONS = [
  "Acima de R$ 3.000/mês",
  "De R$ 2.000 a R$ 3.000/mês",
  "De R$ 450 a R$ 2.000/mês",
  "Até R$ 450/mês",
];

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
  "Ainda em definição",
];

export default function SchoolQuoteModal({ onClose, variant = "educacional" }) {
  const cfg = CFG[variant] || CFG.educacional;
  const isIntercambio = variant === "intercambio";
  const isCruzeiros = variant === "cruzeiros";
  const useWhatsappField = isIntercambio || isCruzeiros;
  const [name, setName] = useState("");
  const [institution, setInstitution] = useState("");
  const [role, setRole] = useState("");
  const [segment, setSegment] = useState(cfg.segmentOptions[0]);
  const [participants, setParticipants] = useState(isIntercambio ? 2000 : (isCruzeiros ? 2 : 30));
  const [mes, setMes] = useState("");
  const [budget, setBudget] = useState("");
  const [destination, setDestination] = useState("");
  const [goal, setGoal] = useState("");
  const [sending, setSending] = useState(false);
  // Intercâmbio-only extra fields
  const [decisor, setDecisor] = useState(DECISOR_OPTIONS[0]);
  const [embarque, setEmbarque] = useState(EMBARQUE_OPTIONS[1]);
  const [investMensal, setInvestMensal] = useState(INVEST_OPTIONS[1]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const canSend = participants > 0 && (name.trim() || institution.trim());

  const submit = () => {
    if (!canSend) return;
    setSending(true);
    const budgetLine = budget
      ? `R$ ${Number(budget).toLocaleString("pt-BR")}${isIntercambio ? " (total)" : (isCruzeiros ? "/pessoa" : "/aluno")}`
      : "A definir";
    const totalLine = !isIntercambio && budget && participants
      ? `\n*Orçamento total estimado:* R$ ${(Number(budget) * Number(participants)).toLocaleString("pt-BR")}`
      : "";
    const lines = [
      "Olá, Orion!",
      "",
      cfg.ctaSummaryWa,
      "",
      `*Contato:* ${name || "—"}`,
      role ? `*${useWhatsappField ? "WhatsApp" : "Cargo"}:* ${role}` : "",
      useWhatsappField
        ? `*Cidade / Estado:* ${institution || "—"}`
        : `*Instituição:* ${institution || "—"}`,
      `*Segmento:* ${segment}`,
      isIntercambio
        ? `*Ano de nascimento:* ${participants}`
        : (isCruzeiros
            ? `*Nº de passageiros:* ${participants}`
            : `*Nº de alunos:* ${participants}`),
      isIntercambio ? `*Decisão:* ${decisor}` : "",
      isIntercambio
        ? `*Previsão de embarque:* ${embarque}`
        : `*Período desejado:* ${mes || "A definir"}`,
      isIntercambio
        ? `*Investimento mensal:* ${investMensal}`
        : (isCruzeiros
            ? `*Investimento por pessoa:* ${budgetLine}`
            : `*Orçamento por aluno:* ${budgetLine}${totalLine}`),
      destination ? `*Destino de interesse:* ${destination}` : "",
      goal ? `*Objetivo:* ${goal}` : "",
      "",
      isIntercambio
        ? "Podem enviar a consultoria de intercâmbio com programa sugerido + orçamento + cronograma?"
        : (isCruzeiros
            ? "Podem enviar 2 a 3 cruzeiros comparados + transfer SP→Santos + cronograma de pagamento?"
            : "Podem enviar a proposta pedagógica + logística + orçamento?"),
      cfg.savingsLine,
    ];
    const text = lines.filter(Boolean).join("\n");

    // Extract phone from WHATSAPP_URL (e.g. https://wa.me/5511XXXXXXXXX)
    let phone = "5511920043024";
    try {
      const m = WHATSAPP_URL.match(/wa\.me\/(\d+)/);
      if (m) phone = m[1];
    } catch { /* fall through with default */ }
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
    setSending(false);
    onClose();
  };

  return (
    <div className="sq-overlay" onClick={onClose} data-testid="sq-modal">
      <div className="sq-modal" onClick={(e) => e.stopPropagation()}>
        <button className="sq-close" onClick={onClose} aria-label="Fechar" data-testid="sq-close">
          <X size={18} />
        </button>
        <header className="sq-head">
          <div className="sq-eyebrow">{cfg.eyebrow}</div>
          <h2 className="sq-title">{cfg.title}</h2>
          <p className="sq-sub">{cfg.subtitle}</p>
        </header>

        <div className="sq-body">
          <div className="sq-grid">
            <label className="sq-field">
              <span>{cfg.contactLabel} *</span>
              <input value={name} onChange={(e) => setName(e.target.value)} type="text" placeholder="Ex.: Sandra Andrade" data-testid="sq-name" />
            </label>
            {!useWhatsappField && (
              <label className="sq-field">
                <span>Cargo</span>
                <input value={role} onChange={(e) => setRole(e.target.value)} type="text" placeholder="Coordenadora Pedagógica" data-testid="sq-role" />
              </label>
            )}
            {useWhatsappField && (
              <label className="sq-field">
                <span>WhatsApp / Telefone *</span>
                <input value={role} onChange={(e) => setRole(e.target.value)} type="tel" placeholder="(11) 91234-5678" data-testid="sq-role" />
              </label>
            )}
            <label className="sq-field sq-col-2">
              <span>{cfg.institutionLabel} *</span>
              <input
                value={institution}
                onChange={(e) => setInstitution(e.target.value)}
                type="text"
                placeholder={useWhatsappField ? "Ex.: São Paulo — SP" : "Ex.: Colégio Objetivo — Unidade Jundiaí"}
                data-testid="sq-institution"
              />
            </label>
            <label className="sq-field">
              <span>{cfg.segmentLabel}</span>
              <select value={segment} onChange={(e) => setSegment(e.target.value)} data-testid="sq-segment">
                {cfg.segmentOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="sq-field">
              <span>{cfg.participantsLabel} *</span>
              <input
                value={participants}
                onChange={(e) => setParticipants(Math.max(1, Number(e.target.value) || 1))}
                type="number"
                min={isIntercambio ? "1900" : "1"}
                max={isIntercambio ? new Date().getFullYear() : undefined}
                placeholder={isIntercambio ? "Ex.: 2001" : ""}
                data-testid="sq-participants"
              />
            </label>
            {isIntercambio ? (
              <>
                <label className="sq-field">
                  <span>Quando pretende embarcar? *</span>
                  <select value={embarque} onChange={(e) => setEmbarque(e.target.value)} data-testid="sq-embarque">
                    {EMBARQUE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
                <label className="sq-field sq-col-2">
                  <span>Quem decidirá seu intercâmbio? *</span>
                  <select value={decisor} onChange={(e) => setDecisor(e.target.value)} data-testid="sq-decisor">
                    {DECISOR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
                <label className="sq-field sq-col-2">
                  <span>Quanto pretende investir por mês? *</span>
                  <select value={investMensal} onChange={(e) => setInvestMensal(e.target.value)} data-testid="sq-invest">
                    {INVEST_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              </>
            ) : (
              <>
                <label className="sq-field">
                  <span>Mês previsto</span>
                  <select value={mes} onChange={(e) => setMes(e.target.value)} data-testid="sq-month">
                    <option value="">Escolher mês…</option>
                    {MESES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
                <label className="sq-field">
                  <span>{isCruzeiros ? "Investimento por pessoa (R$)" : "Orçamento por aluno (R$)"}</span>
                  <input value={budget} onChange={(e) => setBudget(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="Opcional" data-testid="sq-budget" />
                </label>
              </>
            )}
            <label className="sq-field sq-col-2">
              <span>{cfg.destinationLabel}</span>
              <input value={destination} onChange={(e) => setDestination(e.target.value)} type="text" placeholder={cfg.destinationPlaceholder} data-testid="sq-destination" />
            </label>
            <label className="sq-field sq-col-2">
              <span>{cfg.goalLabel}</span>
              <textarea value={goal} onChange={(e) => setGoal(e.target.value)} rows={3} placeholder={cfg.goalPlaceholder} data-testid="sq-goal" />
            </label>
          </div>

          <button className="sq-submit" disabled={!canSend || sending} onClick={submit} data-testid="sq-submit">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Enviar via WhatsApp
          </button>
          <p className="sq-fine">Ao enviar, o pedido é aberto no WhatsApp da Orion com todos os dados já preenchidos.</p>
        </div>
      </div>
      <style>{`
        .sq-overlay {
          position: fixed; inset: 0; z-index: 100;
          background: rgba(4, 1, 15, 0.7);
          backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          animation: sq-fade 200ms ease;
        }
        .sq-modal {
          position: relative;
          width: min(880px, 100%);
          max-height: 92vh;
          overflow-y: auto;
          background: #FFFFFF;
          border-radius: 14px;
          box-shadow: 0 40px 90px -20px rgba(44, 17, 57, 0.55);
          animation: sq-slide 240ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .sq-close {
          position: absolute; top: 14px; right: 14px;
          width: 34px; height: 34px; border-radius: 50%;
          border: 1px solid #EFEAE0; background: #FFF; color: #2C1139;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 180ms;
          z-index: 2;
        }
        .sq-close:hover { border-color: #FF7A2E; color: #FF7A2E; }
        .sq-head {
          padding: 32px 32px 22px 32px;
          background: linear-gradient(135deg, #FAF7F0, #FFFFFF);
          border-bottom: 1px solid #EFEAE0;
          border-radius: 14px 14px 0 0;
        }
        .sq-eyebrow {
          font-size: 11px; letter-spacing: 0.28em; text-transform: uppercase;
          color: #FF7A2E; font-weight: 700; margin-bottom: 6px;
        }
        .sq-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(22px, 3vw, 30px);
          color: #2C1139; line-height: 1.15; margin: 0;
        }
        .sq-sub { margin-top: 8px; font-size: 14px; color: #594A63; line-height: 1.6; }
        .sq-body { padding: 24px 32px 32px 32px; }
        .sq-grid {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 16px 20px;
        }
        .sq-field { display: flex; flex-direction: column; gap: 6px; }
        .sq-field.sq-col-2 { grid-column: span 2; }
        .sq-field > span {
          font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase;
          color: #594A63; font-weight: 600;
        }
        .sq-field input, .sq-field textarea, .sq-field select {
          font: inherit; font-size: 14px;
          padding: 10px 12px;
          background: #FFF; color: #2C1139;
          border: 1px solid #EFEAE0; border-radius: 6px;
          transition: border-color 180ms, box-shadow 180ms;
        }
        .sq-field input:focus, .sq-field textarea:focus, .sq-field select:focus {
          outline: none; border-color: #FF7A2E;
          box-shadow: 0 0 0 3px rgba(255, 122, 46, 0.12);
        }
        .sq-submit {
          margin-top: 22px; width: 100%;
          display: flex; align-items: center; justify-content: center; gap: 10px;
          padding: 14px 20px;
          background: linear-gradient(120deg, #FFCB6B, #FF9E3E 50%, #FF7A2E);
          color: #1a0a26; border: 0; border-radius: 8px;
          font-size: 12px; letter-spacing: 0.22em; text-transform: uppercase;
          font-weight: 800; cursor: pointer;
          box-shadow: 0 12px 28px -10px rgba(255, 122, 46, 0.55);
          transition: transform 180ms, box-shadow 220ms, filter 180ms;
        }
        .sq-submit:hover:not(:disabled) {
          transform: translateY(-1px); filter: brightness(1.05);
          box-shadow: 0 18px 40px -10px rgba(255, 122, 46, 0.8);
        }
        .sq-submit:disabled { opacity: 0.5; cursor: not-allowed; }
        .sq-fine {
          margin-top: 10px; font-size: 11px; color: #8A8191; text-align: center;
        }
        @keyframes sq-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes sq-slide { from { opacity: 0; transform: translateY(24px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @media (max-width: 640px) {
          .sq-grid { grid-template-columns: 1fr; }
          .sq-field.sq-col-2 { grid-column: span 1; }
          .sq-head, .sq-body { padding-left: 20px; padding-right: 20px; }
        }
      `}</style>
    </div>
  );
}
